import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { collection, addDoc, serverTimestamp, getDocs, query, where } from 'firebase/firestore';
import { db } from './firebase';
import { collectionPath } from './firestoreCollections';
import { generateTimestampId } from './documentIdUtils';
import { logUserAction } from './logger';

/**
 * Fire-and-forget audit log for a data export.
 * All calls are wrapped in try/catch so a logging failure never blocks the export itself.
 *
 * @param {object} meta
 * @param {string} meta.filename - Base filename (no extension) — used for both details text and entityId.
 * @param {string} meta.format - Export format ('csv' | 'xlsx' | 'pdf').
 * @param {number} meta.recordCount - Number of records exported.
 * @param {string} [meta.entityType='Export'] - Optional entity type override (e.g. 'Customer', 'Order').
 * @param {string} [meta.mode='immediate'] - 'immediate' (browser) or 'background' (queued job).
 * @param {string} [meta.extra] - Optional extra descriptor appended to details.
 */
function logExportAction({ filename, format, recordCount, entityType = 'Export', mode = 'immediate', extra = '' } = {}) {
    try {
        const fmt = (format || 'unknown').toString().toUpperCase();
        const count = Number.isFinite(recordCount) ? recordCount : (recordCount != null ? Number(recordCount) : 'unknown');
        const modeLabel = mode === 'background' ? ' (background job)' : '';
        const suffix = extra ? ` — ${extra}` : '';
        // Intentionally not awaited: exports must not block on logging.
        logUserAction({
            action: 'Data Exported',
            details: `Exported ${count} records as ${fmt}${modeLabel}: ${filename}${suffix}`,
            entityType,
            entityId: filename || `export_${Date.now()}`,
        }).catch(err => console.warn('Failed to log export action:', err));
    } catch (err) {
        console.warn('logExportAction threw synchronously:', err);
    }
}

/**
 * Export size limits
 * IMMEDIATE: Export runs immediately in browser
 * WARNING: Show warning to user but allow export
 * BACKGROUND: Should use background export system (requires backend)
 */
export const EXPORT_LIMITS = {
  CSV: {
    IMMEDIATE: 5000,
    WARNING: 10000,
    BACKGROUND: 20000
  },
  EXCEL: {
    IMMEDIATE: 3000,
    WARNING: 5000,
    BACKGROUND: 10000
  },
  PDF: {
    IMMEDIATE: 500,
    WARNING: 1000,
    BACKGROUND: 2000
  }
};

/**
 * Check if export size exceeds limits
 * @param {string} format - Export format (csv, xlsx, pdf)
 * @param {number} recordCount - Number of records to export
 * @returns {object} { allowed, level, message }
 */
export function checkExportSize(format, recordCount) {
  const formatKey = format.toUpperCase().replace('XLSX', 'EXCEL');
  const limits = EXPORT_LIMITS[formatKey];
  
  if (!limits) {
    return { allowed: true, level: 'immediate', message: null };
  }
  
  if (recordCount <= limits.IMMEDIATE) {
    return { allowed: true, level: 'immediate', message: null };
  }
  
  if (recordCount <= limits.WARNING) {
    return {
      allowed: true,
      level: 'warning',
      message: `⚠️ Large Export Warning\n\nYou are exporting ${recordCount.toLocaleString()} records to ${format.toUpperCase()}.\n\nThis may take 10-30 seconds and could slow down your browser.\n\nRecommendation: Use filters to reduce the dataset or export to CSV instead.\n\nContinue?`
    };
  }
  
  if (recordCount <= limits.BACKGROUND) {
    return {
      allowed: true,
      level: 'background',
      message: `📤 Background Export Required\n\nYou are exporting ${recordCount.toLocaleString()} records to ${format.toUpperCase()}.\n\nThis exceeds the browser limit of ${limits.WARNING.toLocaleString()} records.\n\nThis export will be processed in the background and you'll receive an email with a download link when ready (typically 5-10 minutes).\n\nContinue with background export?`
    };
  }
  
  return {
    allowed: false,
    level: 'blocked',
    message: `🚫 Export Blocked\n\nYou are trying to export ${recordCount.toLocaleString()} records.\n\nMaximum limit: ${limits.BACKGROUND.toLocaleString()} records.\n\nPlease apply filters to reduce the dataset.`
  };
}

/**
 * Ensure filename has correct extension
 * @param {string} filename - Original filename
 * @param {string} format - Export format (csv, xlsx, pdf)
 * @returns {string} Filename with correct extension
 */
function ensureExtension(filename, format) {
    const ext = `.${format.toLowerCase()}`;
    if (filename.toLowerCase().endsWith(ext)) {
        return filename;
    }
    // Remove any existing extension
    const baseName = filename.replace(/\.[^.]+$/, '');
    return `${baseName}${ext}`;
}

/**
 * Download a blob as a file
 * @param {Blob} blob - The blob to download
 * @param {string} filename - The filename for download
 */
function downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

/**
 * Export array of objects to CSV.
 * @param {string} filename - The file name for download.
 * @param {string[]} headers - Array of column headers.
 * @param {object[]} data - Array of objects to export.
 * @param {function} rowMapper - Function that maps an object to an array of values (matching headers).
 */
export function exportToCSV(filename, headers, data, rowMapper) {
    const rows = data.map(rowMapper);
    const csvContent = [headers, ...rows].map(e => e.map(v => {
        // Escape quotes and wrap in quotes
        const str = v != null ? String(v) : '';
        return `"${str.replace(/"/g, '""')}"`;
    }).join(",")).join("\n");
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    downloadBlob(blob, ensureExtension(filename, 'csv'));
}

/**
 * Export array of objects to Excel (XLSX).
 * @param {string} filename - The file name for download.
 * @param {string[]} headers - Array of column headers.
 * @param {object[]} data - Array of objects to export.
 * @param {function} rowMapper - Function that maps an object to an array of values (matching headers).
 * @param {object} options - Additional options (sheetName, columnWidths)
 */
export function exportToExcel(filename, headers, data, rowMapper, options = {}) {
    const { sheetName = 'Sheet1', columnWidths } = options;
    
    const rows = data.map(rowMapper);
    const worksheetData = [headers, ...rows];
    
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    
    // Set column widths if provided
    if (columnWidths && Array.isArray(columnWidths)) {
        worksheet['!cols'] = columnWidths.map(w => ({ wch: w }));
    }
    
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    
    XLSX.writeFile(workbook, ensureExtension(filename, 'xlsx'));
}

/**
 * Export array of objects to PDF.
 * @param {string} filename - The file name for download.
 * @param {string[]} headers - Array of column headers.
 * @param {object[]} data - Array of objects to export.
 * @param {function} rowMapper - Function that maps an object to an array of values (matching headers).
 * @param {object} options - Additional options (title, orientation, pageSize, styles)
 */
export function exportToPDF(filename, headers, data, rowMapper, options = {}) {
    const {
        title = 'Export',
        orientation = 'landscape', // 'portrait' or 'landscape'
        pageSize = 'a4', // 'a4', 'letter', etc.
        styles = {},
        columnStyles = {},
        fontSize = 7, // Smaller default font for dense data
        maxWidth = null // Maximum width for text wrapping
    } = options;
    
    const rows = data.map(rowMapper);
    
    // Create PDF with specified orientation and page size
    const doc = new jsPDF({
        orientation,
        unit: 'pt',
        format: pageSize
    });
    
    // Calculate page width for column sizing
    const pageWidth = doc.internal.pageSize.getWidth();
    const marginLeft = 20;
    const marginRight = 20;
    const availableWidth = pageWidth - marginLeft - marginRight;
    
    // Intelligent column width calculation
    const numColumns = headers.length;
    const autoColumnStyles = { ...columnStyles };
    
    // If no custom columnStyles provided, calculate smart widths
    if (Object.keys(columnStyles).length === 0 && numColumns > 0) {
        // Estimate average width per column
        const avgWidth = availableWidth / numColumns;
        
        // Set minimum widths based on column count to prevent text wrapping issues
        const minWidth = Math.max(avgWidth * 0.8, 30); // Minimum 30pt per column
        
        for (let i = 0; i < numColumns; i++) {
            autoColumnStyles[i] = {
                cellWidth: avgWidth,
                minCellWidth: minWidth,
                overflow: 'linebreak',
                cellPadding: 2
            };
        }
    }
    
    // Add title
    doc.setFontSize(14);
    doc.text(title, marginLeft, 30);
    
    // Add table with optimized settings
    doc.autoTable({
        head: [headers],
        body: rows,
        startY: 45,
        styles: {
            fontSize: fontSize,
            cellPadding: 2,
            overflow: 'linebreak',
            cellWidth: 'auto',
            minCellHeight: 15,
            halign: 'left',
            valign: 'middle',
            ...styles
        },
        columnStyles: autoColumnStyles,
        headStyles: {
            fillColor: [41, 128, 185],
            textColor: 255,
            fontStyle: 'bold',
            halign: 'center',
            fontSize: fontSize + 1
        },
        alternateRowStyles: {
            fillColor: [245, 245, 245]
        },
        margin: { top: 45, left: marginLeft, right: marginRight, bottom: 30 },
        tableWidth: 'auto',
        theme: 'grid'
    });
    
    doc.save(ensureExtension(filename, 'pdf'));
}

/**
 * Unified export function that supports multiple formats
 * @param {object} config - Export configuration
 * @param {string} config.filename - Base filename without extension
 * @param {string} config.format - Export format: 'csv', 'xlsx', or 'pdf'
 * @param {string[]} config.headers - Array of column headers
 * @param {object[]} config.data - Array of objects to export
 * @param {function} config.rowMapper - Function that maps an object to an array of values
 * @param {object} config.options - Format-specific options
 */
export function exportData(config) {
    const { filename, format, headers, data, rowMapper, options = {}, logMeta = {} } = config;
    
    if (!filename || !headers || !data || !rowMapper) {
        throw new Error('Missing required export parameters: filename, headers, data, and rowMapper are required');
    }
    
    switch (format.toLowerCase()) {
        case 'csv':
            exportToCSV(filename, headers, data, rowMapper);
            break;
        case 'xlsx':
        case 'excel':
            exportToExcel(filename, headers, data, rowMapper, options);
            break;
        case 'pdf':
            exportToPDF(filename, headers, data, rowMapper, options);
            break;
        default:
            throw new Error(`Unsupported export format: ${format}. Supported formats: csv, xlsx, pdf`);
    }

    // Audit log (non-blocking)
    logExportAction({
        filename,
        format,
        recordCount: Array.isArray(data) ? data.length : 0,
        entityType: logMeta.entityType || 'Export',
        mode: 'immediate',
        extra: logMeta.extra || '',
    });
}

/**
 * Export multiple sections of data to a single file.
 * - XLSX: Each section becomes a separate sheet
 * - CSV: Sections are separated by blank rows with section headers
 * - PDF: Each section becomes a separate auto-table with a heading
 *
 * @param {object} config
 * @param {string} config.filename - Base filename without extension
 * @param {string} config.format - Export format: 'csv', 'xlsx', or 'pdf'
 * @param {Array<{name: string, headers: string[], data: object[], rowMapper: function, columnWidths?: number[]}>} config.sections
 * @param {object} [config.options] - Format-specific options (title, orientation, etc.)
 */
export function exportMultiSectionData(config) {
    const { filename, format, sections, options = {}, logMeta = {} } = config;

    if (!filename || !sections || sections.length === 0) {
        throw new Error('Missing required parameters: filename and sections are required');
    }

    const fmt = format.toLowerCase();

    if (fmt === 'xlsx' || fmt === 'excel') {
        // Multi-sheet workbook
        const workbook = XLSX.utils.book_new();
        sections.forEach((section) => {
            const rows = section.data.map(section.rowMapper);
            const wsData = [section.headers, ...rows];
            const ws = XLSX.utils.aoa_to_sheet(wsData);
            if (section.columnWidths) {
                ws['!cols'] = section.columnWidths.map(w => ({ wch: w }));
            }
            // Sheet names are limited to 31 chars in Excel
            const sheetName = (section.name || 'Sheet').substring(0, 31);
            XLSX.utils.book_append_sheet(workbook, ws, sheetName);
        });
        XLSX.writeFile(workbook, ensureExtension(filename, 'xlsx'));
    } else if (fmt === 'csv') {
        // Concatenate sections with blank-row separators and section headers
        const csvParts = [];
        sections.forEach((section, idx) => {
            if (idx > 0) csvParts.push(''); // blank line between sections
            csvParts.push(`"--- ${section.name} ---"`);
            const rows = section.data.map(section.rowMapper);
            [section.headers, ...rows].forEach(row => {
                csvParts.push(row.map(v => {
                    const str = v != null ? String(v) : '';
                    return `"${str.replace(/"/g, '""')}"`;
                }).join(','));
            });
        });
        const blob = new Blob([csvParts.join('\n')], { type: 'text/csv;charset=utf-8;' });
        downloadBlob(blob, ensureExtension(filename, 'csv'));
    } else if (fmt === 'pdf') {
        const {
            title = 'Export',
            orientation = 'landscape',
            pageSize = 'a4',
            fontSize = 7
        } = options;

        const doc = new jsPDF({ orientation, unit: 'pt', format: pageSize });
        let startY = 30;

        // Main title
        doc.setFontSize(14);
        doc.text(title, 20, startY);
        startY += 20;

        sections.forEach((section, idx) => {
            if (idx > 0) {
                // Add new page for subsequent sections
                doc.addPage();
                startY = 30;
            }

            // Section heading
            doc.setFontSize(11);
            doc.text(section.name, 20, startY);
            startY += 15;

            const rows = section.data.map(section.rowMapper);
            doc.autoTable({
                head: [section.headers],
                body: rows,
                startY,
                styles: { fontSize, cellPadding: 2, overflow: 'linebreak' },
                headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: 'bold', halign: 'center', fontSize: fontSize + 1 },
                alternateRowStyles: { fillColor: [245, 245, 245] },
                margin: { top: 45, left: 20, right: 20, bottom: 30 },
                tableWidth: 'auto',
                theme: 'grid'
            });

            // Update startY for next table if on same page
            startY = doc.lastAutoTable.finalY + 25;
        });

        doc.save(ensureExtension(filename, 'pdf'));
    } else {
        throw new Error(`Unsupported export format: ${format}`);
    }

    // Audit log (non-blocking). Sum record counts across sections.
    const totalRecords = sections.reduce((sum, s) => sum + (Array.isArray(s.data) ? s.data.length : 0), 0);
    const sectionNames = sections.map(s => s.name).filter(Boolean).join(', ');
    logExportAction({
        filename,
        format,
        recordCount: totalRecords,
        entityType: logMeta.entityType || 'Export',
        mode: 'immediate',
        extra: logMeta.extra || (sectionNames ? `sections: ${sectionNames}` : ''),
    });
}

/**
 * React hook for export functionality with format selection
 * Returns an object with exportData function and format state management
 * @param {string} baseFilename - Base filename without extension
 * @param {string[]} headers - Array of column headers
 * @param {function} dataGetter - Function that returns the data array to export
 * @param {function} rowMapper - Function that maps an object to an array of values
 * @param {object} defaultOptions - Default options for each format
 * @returns {object} Export handler and utilities
 */
export function createExportHandler(baseFilename, headers, dataGetter, rowMapper, defaultOptions = {}) {
    return {
        /**
         * Execute export with specified format
         * @param {string} format - Export format: 'csv', 'xlsx', or 'pdf'
         */
        handleExport: (format) => {
            const data = dataGetter();
            if (!data || data.length === 0) {
                throw new Error('No data available to export');
            }
            
            exportData({
                filename: baseFilename,
                format,
                headers,
                data,
                rowMapper,
                options: defaultOptions[format] || {}
            });
        }
    };
}

/**
 * Request a background export job for large datasets
 * Creates a Firestore document that triggers Cloud Function processing
 * User receives email with download link when export completes
 * 
 * @param {Object} params - Export job parameters
 * @param {string} params.exportType - Type of data being exported (customers, orders, opportunities, contacts)
 * @param {string} params.format - Export format (csv, xlsx, pdf)
 * @param {number} params.recordCount - Number of records to export
 * @param {Object} params.filters - Applied filters (search, dateRange, status, etc.)
 * @param {string} params.userId - Current user's ID
 * @param {string} params.userEmail - Current user's email address
 * @param {string} params.fileName - Base filename for the export
 * @returns {Promise<string>} - Job ID for tracking
 */
export async function requestBackgroundExport({
    exportType,
    format,
    recordCount,
    filters = {},
    userId,
    userEmail,
    fileName
}) {
    try {
        // Check active jobs for rate limiting (max 3 concurrent)
        const activeJobsSnapshot = await getDocs(
            query(
                collection(db, collectionPath('exportJobs')),
                where('userId', '==', userId),
                where('status', 'in', ['pending', 'processing'])
            )
        );
        
        if (activeJobsSnapshot.size >= 3) {
            throw new Error('You have reached the maximum of 3 concurrent export jobs. Please wait for existing exports to complete.');
        }
        
        // Generate unique job ID
        const jobId = generateTimestampId(`export_${exportType}_${format}`);
        
        // Calculate expiration date (7 days from now)
        const expiresAt = new Date();
        expiresAt.setDate(expiresAt.getDate() + 7);
        
        // Create export job document
        const jobData = {
            jobId,
            userId,
            userEmail,
            exportType,
            format,
            fileName: `${fileName}.${format}`,
            recordCount,
            filters,
            status: 'pending',
            progress: 0,
            createdAt: serverTimestamp(),
            expiresAt,
            error: null,
            downloadUrl: null
        };
        
        // Add to Firestore - this triggers Cloud Function
        await addDoc(collection(db, collectionPath('exportJobs')), jobData);
        
        console.log('Background export job created:', {
            jobId,
            exportType,
            format,
            recordCount,
            userEmail
        });

        // Audit log the queued background export (non-blocking)
        logExportAction({
            filename: fileName || jobId,
            format,
            recordCount,
            entityType: exportType || 'Export',
            mode: 'background',
            extra: `jobId: ${jobId}`,
        });
        
        return jobId;
    } catch (error) {
        console.error('Failed to create background export job:', error);
        throw new Error(`Failed to request background export: ${error.message}`);
    }
}
